import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useThree } from '@react-three/fiber';
import type * as THREE from 'three';
import type { ChunkKind, ContextId, StarBatch } from '@cosmos/core-types';
import type { OriginManager } from '@cosmos/coords';
import type { FlightController } from '@cosmos/nav';
import type { StreamingPolicy } from '@cosmos/streaming';
import { useSettingsStore } from '@cosmos/app-state';
import { createStarPoints, type StarPoints } from '@cosmos/render-stars';
import type { QualityTier } from '@cosmos/core-types';
import {
  createGalaxyPoints,
  createDustLanes,
  createGalaxyImpostor,
  type GalaxyPoints,
  type DustLanes,
  type GalaxyImpostor,
} from '@cosmos/render-galaxy';
import { PRIORITY_RENDER, PRIORITY_STREAMING, useFrameContext, useQuality } from '@cosmos/scene-host';
import {
  createDustTexture,
  createHiiTexture,
  createImpostorTexture,
  buildDustLanes,
  buildHiiRegions,
} from '../glue/galaxy-assets';
import { milkyWayArmGeometry, milkyWayResolvedParams } from '../glue/milky-way-gen';
import { profileSpan } from '../glue/frame-profiler';
import { procgenOpacityHolder } from '../glue/test-hook';
import { computeProcgenDrawFraction } from '../glue/procgen-draw-budget';
import { tileOutsideFrustum } from '../glue/tile-frustum-cull';
import { tileBelowVisibilityFloor } from '../glue/tile-brightness-cull';
import { effectiveStarExposure, NATURAL_VISIBILITY_PROFILE } from '@cosmos/photometry';
import { pcScales } from '../glue/context-scale';
import { octreePickHolder, type OctreePickMount } from '../glue/octree-pick-feed';

/** Bounding-sphere radius factor for an axis-aligned cube of half-extent h: h·√3. */
const SQRT3 = Math.sqrt(3);

/**
 * TASK-093 diagnostics (read by flythrough4 / `__cosmos`): last-frame octree tile
 * frustum-cull counts. Mutated in place each PRIORITY_RENDER — zero alloc.
 */
export const frustumCullStats = {
  kept: 0,
  culled: 0,
  skippedNoCamera: 0,
};

/**
 * TASK-094 diagnostics: last-frame octree tiles hidden by the brightness/distance
 * cull (frustum-kept tiles only). Mutated in place each PRIORITY_RENDER — zero alloc.
 */
export const brightnessCullStats = { culled: 0 };


/**
 * Galaxy / streaming render tier (TASK-040, §5.8/§5.9). Subscribes to the policy's
 * lifecycle registry and mounts ready batches: octree HYG tiles via render-stars
 * point machinery (they are real stars), the procedural Milky Way via render-galaxy
 * (particle cloud + dust lanes + far-LOD impostor). React owns only the rare
 * mount/unmount (event-driven state); per-frame offsets + cross-fade opacities flow
 * imperatively with zero allocations (§2.2, §9).
 *
 * ## Tier hand-off (composition decision, see TASK-040 notes)
 * The M2 star field + system scene are the galaxy/system representation and must
 * stay visually identical (m2 baselines). So this streaming tier renders only while
 * the camera is in the `universe` context, cross-fading to nothing across the
 * universe⇄galaxy boundary — by which point M2's always-mounted HYG field (which
 * grows continuously from a sub-pixel dot) owns the screen. No blank frame results
 * because that field is never absent. The policy still `update()`s every frame
 * (budgets/stats) regardless of this visual gate.
 *
 * M3 overlap debt (procgen + octree + M2 HYG near Sol) — Phase 4 unification plan:
 * docs/research/phase4-render-tier-handoff.md
 */

// Discrete-LOD blend window for the procgen Milky Way: at fine LOD (near, low
// level) the particle cloud is fully shown; at coarse LOD (far, high level) the
// impostor takes over. Cross-fade between, ~per §5.8.
const LOD_CLOUD_FULL = 2;
const LOD_IMPOSTOR_FULL = 6;

// Arm glow is a subtle hint only — the star cloud carries the spiral (Tier-1 trial).
const DUST_MAX_OPACITY = 0.1;
// HII star-forming knots along the arms (Tier-2).
const HII_MAX_OPACITY = 0.38;
const HII_GLOW_COLOR: readonly [number, number, number] = [1.0, 0.35, 0.72];

// The procgen cloud is rendered with magnitude-based per-star brightness, so at the
// galaxy "view" vantage (~50 kpc) the distance modulus (~18 mag) drives each star's
// brightness to ~1e-9 — mathematically invisible. The exposure uniform multiplies
// AFTER the magnitude term, so a large cloud-only boost (~10^distModulus·0.4) brings
// the additive cloud back to a bright spiral. Tuned for the ~50 kpc vantage; the
// layer fades out (layerFade→0) before the camera gets close enough to blow out.
const CLOUD_EXPOSURE_BOOST = 4e5;

// Galaxy catalog-field exposure boost. The global default exposure (25) is tuned for the
// bright HYG monolith near Sol; measured, it leaves ~98% of the Gaia octree field below the
// visibility threshold (only ~1.6% / 47k of the 3M pack perceptible) — see
// docs/research/gaia-visibility-and-realness-problem.md §3. The galaxy field is dominated by
// faint Gaia stars (90% are mag 10-14), so it needs a higher base sensitivity than the slider
// nominal. Verified live on the 3M pack: ×4 (effective ~100) reveals the stars but they sit at
// the dim floor (mag 8.5-10, brightness ~0.01) so the uplift is subtle; effective ~150-200 is
// where the field "reads as a rich sky". ×6 → effective ~150 at the default slider: Gaia is
// clearly visible without touching the control, with headroom left on the slider. Bright stars
// clamp at flux 1 so they do not blow out. The slider stays a relative trim on top of this base.
//
// TASK-097: the ×6 multiplier now lives in NATURAL_VISIBILITY_PROFILE (ADR-007, galaxy-octree
// layer). There is no mode setting yet, so Natural is hard-selected here and runtime behavior is
// unchanged. Compute effective octree exposure through effectiveStarExposure(NATURAL, …) so the
// renderer and the tile cull share one source for the multiplier.
const galaxyFieldExposure = (sliderExposure: number): number =>
  effectiveStarExposure(NATURAL_VISIBILITY_PROFILE, 'galaxy-octree', sliderExposure);

/**
 * Distance guard (parsecs from the Milky Way centre) so the heavy 1M-point procgen
 * cloud is suppressed near Sol / the inner disc REGARDLESS of the coverage signal.
 * Coverage (ADR-006 §5) is the primary fade, but it is weak with the committed Gaia
 * *sample* pack and lags while tiles stream in — and drawing the full cloud on top of
 * the real catalog near home is the §2/§5.8 redundancy + an additive-overdraw perf
 * trap. Below LO the cloud is off (octree owns the neighbourhood); it ramps to full
 * by the Milky Way vantage. The retired M3 `GAL_PROCGEN_FLOOR` floor is NOT restored —
 * near Sol the cloud truly reaches 0.
 *
 * LO is the MEASURED hand-off where the real catalog stops filling the view: the
 * brightest real star drops below the visibility floor by ≈1.5–2.5 kpc, then 0 visible
 * px out to the old LO=18 kpc — a black "empty band" mid-transit (P1). LO=1500 ramps
 * procgen on right where the real field fades out, so there is no gap parked or flying,
 * while staying 0 below ~1.5 kpc (local hops show only the real field; no overdraw on
 * the dense catalog near Sol — R1). See docs/research/galaxy-transit-procgen-floor-design.md §8.
 */
const GAL_FADE_LO_PC = 1_500;
const GAL_FADE_HI_PC = 45_000;

/**
 * Procgen LOD cap (ADR-006 §5.4 / docs/research/procgen-lod-near-sol.md), now tier-aware
 * (TASK-071 / docs/research/integrated-gpu-targeting.md §3). The Milky Way cloud carries
 * `MILKY_WAY_STAR_COUNT` (1,000,000) points, but procgen has no per-distance LOD: whenever
 * the layer is on it draws up to a per-tier budget. On integrated GPUs the sole cause of the
 * `flythrough4` §5.4 near-Sol regression was the inner approach band (just above
 * `GAL_FADE_LO_PC`) full-draws 1M while the gate budgets ≤109,971 total scene points — hence
 * the `low` tier stays capped at exactly 90_000 (a shipped bug fix; load-bearing, do not
 * change). Capable GPUs (`high` tier) don't need the cap at all: draw the full cloud to
 * restore inter-arm density at the far vantage. `medium` is a placeholder mid-point pending
 * real M1/integrated-GPU calibration (TASK-072+) — it may move once measured. The
 * tier→budget table and the pure `computeProcgenDrawFraction` mapping live in
 * `../glue/procgen-draw-budget` (DOM/THREE-free, so the node-env unit tests can exercise it
 * directly per `vitest.config.ts`'s `src/glue/**` scope).
 *
 * The cloud never NEEDS 1M to read as a dense field: the far vantage is the impostor
 * (cloudFactor→0, lod≥LOD_IMPOSTOR_FULL) and the near field is the real catalog. So cap
 * the DRAWN points to this budget via `setDrawFraction` (a contiguous prefix of the
 * well-mixed seeded placement sequence — a representative uniform thin of the disc, not a
 * bright-core bias). The cap is on COUNT only, at FULL opacity — it does NOT re-create P2
 * ("nebulas without stars"): P2 was the count AND opacity both collapsing together at low
 * blend; here opacity still carries the whole fade and the capped points stay lit under the
 * nebula sprites. Headroom (low tier): 90k cloud + ~5k near-Sol octree + ~600 overlay ≈ 96k
 * < 109,971. `drawFraction` is a PERF-ONLY knob — it must never be derived from or fed by
 * `procgenBlend` (the visual fade); see the contract at the `setDrawFraction` call site below.
 */

/** Octree tile mounts deferred during flight — flushed gradually after arrival. */
const OCTREE_FLUSH_PER_FRAME = 2;
/**
 * Deferred octree drain rate WHILE a goTo flight is active (TASK-079). Higher than the
 * post-arrival rate so the queue keeps pace with decode (bounded by maxInFlight = 6) and
 * the field never reads black mid-flight — the galaxy→star-field fly-in evicts the near
 * tiles on the way out, and at 2/frame the re-stream couldn't remount them before arrival
 * (black on Metal/integrated GPU). Still a hard per-frame ceiling so a decode burst can't
 * spike GPU upload on weak hardware. 8 > maxInFlight, so it mounts what decoded, capped.
 * See docs/research/galaxy-starfield-flyin-black-flush-during-flight.md.
 */
const OCTREE_FLUSH_PER_FRAME_FLYING = 8;

function smoothstep(lo: number, hi: number, x: number): number {
  if (hi <= lo) return x >= hi ? 1 : 0;
  const t = Math.max(0, Math.min(1, (x - lo) / (hi - lo)));
  return t * t * (3 - 2 * t);
}

interface Mount {
  readonly chunkId: string;
  readonly kind: ChunkKind;
  readonly context: ContextId;
  readonly originPc: readonly [number, number, number];
  readonly batch: StarBatch;
  readonly objects: readonly THREE.Object3D[];
  /**
   * Brightest absolute magnitude in the batch (min absMag), scanned once at mount.
   * `Number.NEGATIVE_INFINITY` for empty / procgen — poison value so a mis-read
   * never brightness-culls the tile (TASK-094).
   */
  readonly minAbsMag: number;
  /** Last frame this mount was on the visible cut (hide stale mounts each frame). */
  seen: number;
  /**
   * `offset` is in PARSECS (the renderers' contract), and `pcToUnits` is the parsec →
   * active-context scale the shader applies at the projection. Both are supplied EVERY
   * frame rather than on context change: mounts are created dynamically per streaming
   * tile, so a late mount would otherwise keep the 1.0 default. TASK-081.
   */
  applyFrame(
    offsetPc: readonly [number, number, number],
    opacity: number,
    lod: number,
    pcToUnits: number,
    drawFraction?: number,
  ): void;
  setViewportHeight(px: number): void;
  setExposure(exposure: number): void;
  hide(): void;
  dispose(): void;
}

/** Scan brightest absMag once at mount — zero per-frame cost (TASK-094). */
function scanMinAbsMag(absMag: Float32Array): number {
  let min = Number.POSITIVE_INFINITY;
  for (let i = 0; i < absMag.length; i++) {
    const a = absMag[i]!;
    // NaN entries fail `a < min` and are skipped automatically.
    if (a < min) min = a;
  }
  // Empty (or all-NaN) ⇒ −∞ so the tile is never brightness-culled.
  return min === Number.POSITIVE_INFINITY ? Number.NEGATIVE_INFINITY : min;
}

function makeOctreeMount(
  chunkId: string,
  batch: StarBatch,
  viewportPx: number,
  exposure: number,
): Mount {
  const points: StarPoints = createStarPoints({ batch });
  points.object.frustumCulled = false;
  points.setViewportHeight(viewportPx);
  points.setExposure(galaxyFieldExposure(exposure));
  return {
    chunkId,
    kind: 'octree',
    context: 'galaxy',
    originPc: batch.originPc,
    batch,
    objects: [points.object],
    minAbsMag: scanMinAbsMag(batch.absMag),
    seen: 0,
    applyFrame(offsetPc, opacity, _lod, pcToUnits): void {
      points.object.visible = true;
      points.setContextScale(pcToUnits);
      points.setRenderOffset(offsetPc);
      points.setOpacity(opacity);
    },
    setViewportHeight: (px) => points.setViewportHeight(px),
    setExposure: (e) => points.setExposure(galaxyFieldExposure(e)),
    // Hard hide via object.visible — additive/multiply opacity 0 does NOT remove a
    // draw (and MultiplyBlending at 0 would darken), so toggle visibility instead.
    hide: () => {
      points.object.visible = false;
    },
    dispose: () => points.dispose(),
  };
}

function makeProcgenMount(
  chunkId: string,
  batch: StarBatch,
  viewportPx: number,
  exposure: number,
  dustTexture: THREE.Texture,
  hiiTexture: THREE.Texture,
  impostorTexture: THREE.Texture,
  dust: { centersUnits: Float32Array; radiiUnits: Float32Array },
  hii: { centersUnits: Float32Array; radiiUnits: Float32Array },
): Mount {
  const cloud: GalaxyPoints = createGalaxyPoints({
    batch,
    minPointPx: 2,
    basePointPx: 5,
    maxPointPx: 48,
    armGeometry: milkyWayArmGeometry(),
  });
  cloud.object.frustumCulled = false;
  cloud.setViewportHeight(viewportPx);
  cloud.setExposure(exposure * CLOUD_EXPOSURE_BOOST);
  const lanes: DustLanes = createDustLanes({
    centersUnits: dust.centersUnits,
    radiiUnits: dust.radiiUnits,
    dustTexture,
  });
  lanes.object.frustumCulled = false;
  const hiiRegions: DustLanes = createDustLanes({
    centersUnits: hii.centersUnits,
    radiiUnits: hii.radiiUnits,
    dustTexture: hiiTexture,
    glowColor: HII_GLOW_COLOR,
  });
  hiiRegions.object.frustumCulled = false;
  // TASK-082 D2 — the sprite's size must agree with the LOD about how big the galaxy is, so
  // it comes from the same resolved procgen params the star cloud and streaming's half-extent
  // use (`discRadiusPc`), NOT from the `milkyWayRadiusPc` prop: that is a random local-group
  // draw (rng.range(5, 50) kpc), unrelated to the disc being stood in for and disagreeing
  // 1.87x with the half-extent that decides WHEN the impostor appears.
  const impostorRadiusPc = milkyWayResolvedParams().discRadiusPc;
  const impostor: GalaxyImpostor = createGalaxyImpostor({
    spriteTexture: impostorTexture,
    radiusPc: impostorRadiusPc,
  });
  impostor.object.frustumCulled = false;

  return {
    chunkId,
    kind: 'procgen',
    context: 'galaxy',
    originPc: batch.originPc,
    batch,
    objects: [impostor.object, lanes.object, cloud.object, hiiRegions.object],
    // Poison: infinitely bright — procgen is never brightness-culled (gated on
    // kind === 'octree' upstream); if misread, keep the tile (TASK-094).
    minAbsMag: Number.NEGATIVE_INFINITY,
    seen: 0,
    applyFrame(offsetPc, opacity, lod, pcToUnits, drawFraction = 1): void {
      const cloudFactor = 1 - smoothstep(LOD_CLOUD_FULL, LOD_IMPOSTOR_FULL, lod);
      cloud.setDrawFraction(drawFraction);
      cloud.object.visible = true;
      lanes.object.visible = true;
      hiiRegions.object.visible = true;
      impostor.object.visible = true;
      cloud.setContextScale(pcToUnits);
      cloud.setRenderOffset(offsetPc);
      cloud.setOpacity(opacity * cloudFactor);
      // TASK-081 leaves lanes/hii OUT OF SCOPE: they have no context scale, so outside
      // galaxy context they receive a parsec offset and still project their (parsec)
      // centres/radii as if they were context units — a different wrongness, not a fix,
      // and accepted as such. In galaxy context pcToUnits is exactly 1 and this is a
      // no-op. The lanes/hii follow-up is filed alongside TASK-082.
      lanes.setRenderOffset(offsetPc);
      lanes.setOpacity(opacity * cloudFactor * DUST_MAX_OPACITY);
      hiiRegions.setRenderOffset(offsetPc);
      hiiRegions.setOpacity(opacity * cloudFactor * HII_MAX_OPACITY);
      // TASK-082 — radius AND scale every frame, never once at construction: mounts are
      // created per streaming tile, so one created after the last context change would
      // otherwise keep the constructor's value. Two float-uniform writes, zero alloc.
      impostor.setContextScale(pcToUnits);
      impostor.setRadiusPc(impostorRadiusOverride.current ?? impostorRadiusPc);
      impostor.setRenderOffset(offsetPc);
      impostor.setOpacity(opacity * (1 - cloudFactor));
    },
    setViewportHeight: (px) => cloud.setViewportHeight(px),
    setExposure: (e) => cloud.setExposure(e * CLOUD_EXPOSURE_BOOST),
    hide(): void {
      cloud.object.visible = false;
      lanes.object.visible = false;
      hiiRegions.object.visible = false;
      impostor.object.visible = false;
    },
    dispose(): void {
      cloud.dispose();
      lanes.dispose();
      hiiRegions.dispose();
      impostor.dispose();
    },
  };
}

interface GalaxySceneProps {
  readonly streaming: StreamingPolicy;
  readonly origin: OriginManager;
  readonly controllerRef: RefObject<FlightController | null>;
  /**
   * VESTIGIAL as of TASK-082 (D2/D4): this is a random local-group draw and no longer
   * feeds the impostor, which now sources `discRadiusPc` like the rest of the procgen
   * galaxy. Kept on the props and on all 7 app entry points because removing it is a
   * mechanical change unrelated to that defect — follow-up task.
   */
  readonly milkyWayRadiusPc: number;
}

/**
 * Dev/E2E override for the impostor radius, in PARSECS (`null` = the shipped
 * `discRadiusPc`). Reached from `window.__cosmosDev.setImpostorRadiusPc`.
 *
 * This exists because the impostor's size can only be proven in PIXELS, and the only
 * platform-independent way to do that is to ablate it inside one run and compare frames:
 * an absolute lit-pixel threshold would encode this machine's SwiftShader build. The
 * previous unit test asserted `mesh.scale` instead and passed for a sprite that had never
 * drawn a pixel (docs/research/galaxy-impostor-scale-is-inert.md) — that is the failure
 * this hook exists to prevent recurring. Gate: e2e/tests/universe-impostor-scale.spec.ts.
 */
export const impostorRadiusOverride: { current: number | null } = { current: null };

// Module-scoped scratch — no allocations inside the frame callback (§9).
const posScratch: { context: ContextId; local: [number, number, number] } = {
  context: 'galaxy',
  local: [0, 0, 0],
};
const offScratch: [number, number, number] = [0, 0, 0];

// Octree pick holder publishing (TASK-088 D2). `octreePickVisible` is the reused snapshot of
// Mount refs published last frame; the frame loop rebuilds `octreePickHolder.current` (a fresh
// `OctreePickMount[]`) ONLY when the visible-octree set differs from it — never allocating on an
// unchanged frame (mirrors the imperative low-frequency-write discipline of the other feeds, §9).
const octreePickVisible: Mount[] = [];

export function GalaxyScene({ streaming, origin, controllerRef }: GalaxySceneProps) {
  const size = useThree((s) => s.size);
  const dpr = useThree((s) => s.viewport.dpr);
  const camera = useThree((s) => s.camera) as THREE.PerspectiveCamera;

  // Quality tier drives the procgen draw-fraction cap (TASK-071). useQuality re-renders
  // only on a tier change (pattern copied from Overlays.tsx); the frame loop below is a
  // useFrameContext callback (not a React re-render), so it reads a ref kept in sync by
  // effect, never the hook value directly.
  const tier = useQuality().tier;
  const tierRef = useRef<QualityTier>(tier);
  useEffect(() => {
    tierRef.current = tier;
  }, [tier]);

  // Caller-owned galaxy assets (render-galaxy injects none): built once.
  const assets = useMemo(
    () => ({
      dustTexture: createDustTexture(),
      hiiTexture: createHiiTexture(),
      impostorTexture: createImpostorTexture(),
      dustLanes: buildDustLanes(),
      hiiRegions: buildHiiRegions(),
    }),
    [],
  );
  useEffect(
    () => () => {
      assets.dustTexture.dispose();
      assets.hiiTexture.dispose();
      assets.impostorTexture.dispose();
    },
    [assets],
  );

  // Mount registry: a ref Map (driven by lifecycle events) + a parallel list for
  // zero-alloc per-frame iteration. `version` bumps only on mount/unmount so React
  // re-renders the <primitive> set; it never changes per frame.
  const mounts = useRef<Map<string, Mount>>(new Map());
  const mountList = useRef<Mount[]>([]);
  const [version, setVersion] = useState(0);
  const frameTick = useRef(0);
  const flightActiveRef = useRef(false);
  const deferredOctree = useRef<{ chunkId: string; batch: StarBatch }[]>([]);
  const addMountRef = useRef<(chunkId: string, kind: ChunkKind, batch: StarBatch) => void>(
    () => {},
  );

  // Stable refs to the latest viewport/exposure so the event-driven mount factory
  // can initialise new mounts without re-subscribing the lifecycle listener.
  const viewportPx = useRef(size.height * dpr);
  const exposure = useRef(useSettingsStore.getState().exposure);

  useEffect(() => {
    const addMount = (chunkId: string, kind: ChunkKind, batch: StarBatch): void => {
      if (mounts.current.has(chunkId)) return;
      profileSpan(kind === 'octree' ? 'galaxy.mountOctree' : 'galaxy.mountProcgen', () => {
      const m =
        kind === 'octree'
          ? makeOctreeMount(chunkId, batch, viewportPx.current, exposure.current)
          : makeProcgenMount(
              chunkId,
              batch,
              viewportPx.current,
              exposure.current,
              assets.dustTexture,
              assets.hiiTexture,
              assets.impostorTexture,
              assets.dustLanes,
              assets.hiiRegions,
            );
      m.hide(); // start invisible; the frame loop fades it in via the cut opacity.
      mounts.current.set(chunkId, m);
      mountList.current.push(m);
      setVersion((v) => v + 1);
      });
    };
    addMountRef.current = addMount;
    const removeMount = (chunkId: string): void => {
      const m = mounts.current.get(chunkId);
      if (!m) return;
      mounts.current.delete(chunkId);
      const i = mountList.current.indexOf(m);
      if (i >= 0) mountList.current.splice(i, 1);
      m.dispose();
      setVersion((v) => v + 1);
    };

    const unsub = streaming.onChunk((e) => {
      if (e.phase === 'ready' && e.batch !== null) {
        if (e.kind === 'octree' && flightActiveRef.current) {
          deferredOctree.current.push({ chunkId: e.chunkId, batch: e.batch });
          return;
        }
        addMount(e.chunkId, e.kind, e.batch);
      } else if (e.phase === 'evict') removeMount(e.chunkId);
    });
    return () => {
      unsub();
      deferredOctree.current = [];
      for (const m of mountList.current) m.dispose();
      mountList.current = [];
      mounts.current.clear();
      // Drop the octree pick surface — no visible stream once this scene unmounts (TASK-088).
      octreePickVisible.length = 0;
      octreePickHolder.current = null;
    };
  }, [streaming, assets]);

  // Viewport height → all mounts (and remember for future mounts).
  useEffect(() => {
    const h = size.height * dpr;
    viewportPx.current = h;
    for (const m of mountList.current) m.setViewportHeight(h);
  }, [size.height, dpr, version]);

  // Exposure: transient store subscription — never a React re-render.
  useEffect(() => {
    const apply = (e: number): void => {
      exposure.current = e;
      for (const m of mountList.current) m.setExposure(e);
    };
    apply(useSettingsStore.getState().exposure);
    return useSettingsStore.subscribe((s) => apply(s.exposure));
  }, []);

  // §5.8 brain: the visible cut / fetch / evict decisions, on the main thread,
  // BEFORE render. dtMs is the clamped wall delta from the frame context.
  useFrameContext((ctx) => {
    profileSpan('streaming.update', () => {
      streaming.update(size.height * dpr, ctx.dtMs);
    });
  }, PRIORITY_STREAMING);

  useFrameContext(() => {
    profileSpan('galaxy.render', () => {
    const tick = ++frameTick.current;
    const ctrl = controllerRef.current;
    const ctx: ContextId = ctrl ? ctrl.contextId : origin.context;
    const flying = ctrl?.goToActive ?? false;
    flightActiveRef.current = flying;

    // Flush octree tiles deferred during flight — capped per frame, DURING flight (faster,
    // OCTREE_FLUSH_PER_FRAME_FLYING) as well as after arrival (OCTREE_FLUSH_PER_FRAME). The
    // in-flight drain (TASK-079) keeps the re-streamed near field from reading black through
    // a galaxy→star-field fly-in; the cap keeps GPU upload bounded (see the constant).
    // (The in-flight procgen draw-cap that used to live here was removed: it was the
    // sole cause of P2 — a thinned star cloud under full-opacity nebula sprites,
    // "nebulas without stars" — and protected no measured budget. The resting far
    // vantage already full-draws the 1M-point cloud continuously, so full draw in the
    // mid-band during flight adds no new worst case; near Sol blend≈0 keeps it off
    // anyway. See docs/research/galaxy-transit-procgen-floor-design.md §5 E / §8.)
    const flushCap = flying ? OCTREE_FLUSH_PER_FRAME_FLYING : OCTREE_FLUSH_PER_FRAME;
    for (let n = 0; n < flushCap && deferredOctree.current.length > 0; n++) {
      const p = deferredOctree.current.shift()!;
      addMountRef.current(p.chunkId, 'octree', p.batch);
    }

    // Streaming tier: active in universe + galaxy. ADR-006 §5 wanted the procgen cloud
    // to fade by catalogCoverage() (procgen yields as the real octree tiles cover the
    // cut). That signal is UNUSABLE inside the galaxy with the current data: the octree
    // is galaxy-scale-boxed (rootHalfExtent ≈ 65 kpc) but its stars are Sol-local, so
    // far out the cut collapses to a few COARSE tiles whose geometric boxes fill the
    // screen → coverage saturates to ~1 at every distance → `1 − cov` permanently vetoes
    // the spiral. That made the whole Milky Way INVISIBLE at the ~49 kpc vantage (BUG).
    // Distance is the reliable driver here (Sol is the galaxy-frame origin, so
    // distFromCenter is 0 at home and large far out): smoothstep gives procgen OFF near
    // Sol (catalog owns the view) and full far out (impostor + coarse spiral, ADR-006
    // table). Distance drives the opacity DURING a goTo flight too — the old code took
    // min(coverageFade, distanceFade) while flying, but coverageFade saturates to 0
    // in-galaxy (cov→1, see above) so that min pinned the spiral to 0 for the WHOLE
    // breadcrumb flight: the camera flew through the band (where distanceFade
    // is non-zero) rendering black, and the spiral only popped in once it parked
    // (flying→false). Using distanceFade alone fades the spiral in along the trajectory.
    // The near-Sol flight budget (flythrough4 §5.4) is still protected: below GAL_FADE_LO_PC
    // distanceFade is 0, so the cloud is off near home.
    //
    // Procgen-visibility contract (anti-regression — see design doc §6). The whole
    // procgen LAYER (cloud + dust lanes + HII + impostor) shares one blend so stars and
    // nebulas are always visible TOGETHER — never sprites floating in black (P2):
    //   - Parked near Sol (< GAL_FADE_LO_PC ~1.5 kpc): real catalog owns the view,
    //     procgen OFF (blend 0) — no overdraw on the dense catalog (R1, R4).
    //   - Mid band (LO->HI): real field is sub-pixel; procgen ramps on, fills P1.
    //   - Far vantage (>= HI): real field gone, full spiral (R2).
    //   - In flight, any distance: same as parked at that distance — no extra
    //     suppression that empties the view (P1/P3).
    // See docs/research/galaxy-transit-procgen-floor-design.md and goto-galaxy-transit-black.md.
    let procgenBlend = 1;
    if (ctx === 'galaxy') {
      const cov = streaming.catalogCoverage();
      const coverageFade = Math.max(0, Math.min(1, 1 - cov));
      if (ctrl) {
        const p = ctrl.state.position.local;
        const distFromCenterPc = Math.hypot(p[0], p[1], p[2]);
        const distanceFade = smoothstep(GAL_FADE_LO_PC, GAL_FADE_HI_PC, distFromCenterPc);
        procgenBlend = distanceFade;
      } else {
        procgenBlend = coverageFade;
      }
    }
    procgenOpacityHolder.current = procgenBlend;

    // drawFraction is the procgen LOD knob (how many of the cloud's points to draw),
    // capped per-tier via PROCGEN_MAX_DRAW_POINTS_BY_TIER — a perf/budget LOD, NOT part of
    // the visual fade. It is DISTANCE-INDEPENDENT and FULL-OPACITY: opacity (= procgenBlend)
    // remains the sole visibility factor, shared by the cloud AND the nebula sprites so they
    // fade together. This is why it does not re-create P2 ("nebulas without stars"): P2 tied
    // the count to the BLEND, so at low blend the cloud was doubly dimmed (few points ×
    // low opacity) while the fixed-count sprites survived. Here the count cap is fixed per
    // tier (always lit at the layer's full opacity); below GAL_FADE_LO_PC the whole layer is
    // hidden (skipped below). The cap is applied per-mount from its own point count via
    // `computeProcgenDrawFraction`, so a mount carrying ≤ the tier's budget draws in full
    // (fraction 1) — a live tier change (PerformanceMonitor stepping up/down) re-runs this
    // computation every frame from the mount's own current count, never a stale value.
    // See docs/research/procgen-lod-near-sol.md and docs/research/integrated-gpu-targeting.md §3.
    const procgenLayerOn = procgenBlend > 0.0001;
    const opacityBlend = flying ? Math.min(1, procgenBlend * 1.15) : procgenBlend;

    // TASK-081 unit bridge — exactly 1/1 in galaxy context (bit-identical there).
    const { unitsToPc, pcToUnits } = pcScales(ctx);
    const streamingActive = ctx === 'universe' || ctx === 'galaxy';
    const visible = streaming.visible;
    // TASK-093: live PerspectiveCamera fov/aspect (what the shader projects with) —
    // NOT streaming's fixed STREAM_VERTICAL_FOV_RAD. Computed once per frame.
    const tanY = Math.tan((camera.fov * Math.PI) / 360);
    const tanX = tanY * camera.aspect;
    const orientation = ctrl?.state.orientation ?? null;
    let cullKept = 0;
    let cullCulled = 0;
    let cullSkipped = 0;
    let brightnessCulled = 0;
    if (streamingActive) {
      for (let i = 0; i < visible.length; i++) {
        const v = visible[i]!;
        const m = mounts.current.get(v.chunkId);
        if (m === undefined) continue;
        // Already-mounted octree tiles stay drawn during a goTo flight. An earlier guard
        // here (`if (flying && m.kind === 'octree') continue`) hid them while flying, which
        // blanked the real Gaia field for the WHOLE of every goto — the camera flew through
        // a black void and the catalog snapped back only on arrival (measured: max luma
        // 3/255, 0 visible px during a Sol→Betelgeuse goto). It saved no draw calls (the cut
        // is the same either way) and did NOT protect the flythrough4 §5.4 budget — that
        // probe replays the path directly and never sets goToActive, so the guard was inert
        // there. The NEW-tile mount throttle below (deferredOctree) still caps upload cost
        // mid-flight; we just no longer hide what is already on screen.
        // See docs/research/goto-galaxy-transit-black.md §6.
        posScratch.context = m.context;
        posScratch.local[0] = m.originPc[0];
        posScratch.local[1] = m.originPc[1];
        posScratch.local[2] = m.originPc[2];
        origin.toRenderSpace(posScratch, offScratch);
        // TASK-081: the renderers' offset contract is PARSECS; toRenderSpace returns
        // ACTIVE-CONTEXT units. Convert in place at this single site (zero alloc) — every
        // mount downstream receives the parsec offset plus the scale it must apply.
        offScratch[0] *= unitsToPc;
        offScratch[1] *= unitsToPc;
        offScratch[2] *= unitsToPc;
        // TASK-093: per-tile draw-time frustum cull (octree only). Hide without setting
        // `seen` so the trailing hide pass and the octree-pick publish both treat it as
        // off-cut. Tiles stay STREAMED/MOUNTED — only object.visible is gated. Skip the
        // cull when no controller (draw as today — never cull without a camera).
        // TASK-094: brightness/distance cull chains after the frustum-KEPT branch.
        if (m.kind === 'octree') {
          if (orientation === null) {
            cullSkipped += 1;
          } else if (
            tileOutsideFrustum(
              offScratch[0],
              offScratch[1],
              offScratch[2],
              v.halfExtentPc * SQRT3,
              orientation,
              tanX,
              tanY,
            )
          ) {
            cullCulled += 1;
            m.hide();
            continue;
          } else {
            // Frustum-kept: count for TASK-093 diagnostic continuity, then optionally
            // brightness-cull (TASK-094). Drawn-tile peak = kept − brightnessCulled.
            cullKept += 1;
            if (
              tileBelowVisibilityFloor(
                Math.hypot(offScratch[0], offScratch[1], offScratch[2]),
                v.halfExtentPc * SQRT3,
                m.minAbsMag,
                galaxyFieldExposure(exposure.current),
              )
            ) {
              brightnessCulled += 1;
              m.hide();
              continue; // no m.seen = tick — same contract as frustum cull
            }
          }
        }
        m.seen = tick;
        if (m.kind === 'procgen') {
          // Near Sol (blend 0): hide the whole layer so the real catalog owns the view
          // with no procgen overdraw (R1, contract §6). Must hide() explicitly — m.seen
          // was already set to tick above, so the trailing hide pass would skip it and
          // the layer would keep its previous (visible) frame.
          if (!procgenLayerOn) {
            m.hide();
            continue;
          }
          const drawFraction = computeProcgenDrawFraction(tierRef.current, m.batch.count);
          m.applyFrame(offScratch, v.opacity * opacityBlend, v.lod, pcToUnits, drawFraction);
        } else {
          m.applyFrame(offScratch, v.opacity, v.lod, pcToUnits);
        }
      }
    }
    frustumCullStats.kept = cullKept;
    frustumCullStats.culled = cullCulled;
    frustumCullStats.skippedNoCamera = cullSkipped;
    brightnessCullStats.culled = brightnessCulled;
    // Hide any mount not on the visible cut this frame (or whole layer faded out).
    const list = mountList.current;
    for (let i = 0; i < list.length; i++) {
      if (list[i]!.seen !== tick) list[i]!.hide();
    }

    // Publish the currently-visible octree mounts for the star pick (TASK-088 D2). Visible ⟺
    // an octree mount on the cut this frame (`seen === tick`; procgen excluded — synthetic, no
    // real catalogIds). Rebuild the holder only when this set changes vs. last frame, so an
    // unchanged frame allocates nothing.
    const vis = octreePickVisible;
    let changed = false;
    let n = 0;
    for (let i = 0; i < list.length; i++) {
      const m = list[i]!;
      if (m.kind !== 'octree' || m.seen !== tick) continue;
      if (n >= vis.length || vis[n] !== m) changed = true;
      vis[n] = m;
      n++;
    }
    if (n !== vis.length) changed = true;
    vis.length = n;
    if (changed) {
      if (n === 0) {
        octreePickHolder.current = null;
      } else {
        const next: OctreePickMount[] = new Array<OctreePickMount>(n);
        for (let i = 0; i < n; i++) next[i] = { chunkId: vis[i]!.chunkId, batch: vis[i]!.batch };
        octreePickHolder.current = next;
      }
    }
    });
  }, PRIORITY_RENDER);

  return (
    <>
      {mountList.current.flatMap((m) =>
        m.objects.map((o, i) => <primitive key={`${m.chunkId}:${i}`} object={o} />),
      )}
    </>
  );
}
